import { useParams, Link } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { getStoryBySlug } from "~/stories";
import { useBookshelf } from "~/contexts/BookshelfContext";
import { useNightMode } from "~/contexts/NightModeContext";

export default function StoryPage() {
  const { ageGroup, slug } = useParams<{ ageGroup: string; slug: string }>();
  const story = getStoryBySlug(slug || "");
  const { markAsRead, isFavorite, toggleFavorite } = useBookshelf();
  const { isNightMode, toggleNightMode, fontSize, setFontSize } = useNightMode();

  useEffect(() => {
    if (story) markAsRead(story.id);
  }, [story?.id]);

  if (!story) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <h1 className="text-2xl mb-4">Story not found</h1>
        <Link to={ageGroup ? `/age/${ageGroup}` : "/"} className="text-blue-600 hover:underline">
          &larr; Back to stories
        </Link>
      </div>
    );
  }

  const StoryComponent = lazy(story.component);

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isNightMode ? "bg-amber-950 text-amber-100" : "bg-white dark:bg-gray-900 dark:text-white"
      }`}
      style={{ fontSize: `${fontSize}px` }}
    >
      {/* Top bar */}
      <div className={`sticky top-0 z-50 backdrop-blur-md ${isNightMode ? "bg-amber-950/90" : "bg-white/90 dark:bg-gray-900/90"} border-b border-gray-200 dark:border-gray-700`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to={`/age/${story.ageGroup}`}
            className={`text-sm ${isNightMode ? "text-amber-300 hover:text-amber-100" : "text-gray-500 hover:text-gray-700 dark:text-gray-400"}`}
          >
            &larr; Back
          </Link>

          <div className="flex items-center gap-3">
            {/* Font size controls */}
            <button
              onClick={() => setFontSize(Math.max(14, fontSize - 2))}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-xs font-bold"
              title="Decrease text size"
            >
              A-
            </button>
            <button
              onClick={() => setFontSize(Math.min(28, fontSize + 2))}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-bold"
              title="Increase text size"
            >
              A+
            </button>

            {/* Night mode toggle */}
            <button
              onClick={toggleNightMode}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              title={isNightMode ? "Day mode" : "Night mode"}
            >
              {isNightMode ? (
                <svg className="w-5 h-5 text-amber-300" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" /><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" /></svg>
              )}
            </button>

            {/* Favorite toggle */}
            <button
              onClick={() => toggleFavorite(story.id)}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              title={isFavorite(story.id) ? "Remove from bookshelf" : "Add to bookshelf"}
            >
              <svg className="w-5 h-5" fill={isFavorite(story.id) ? "#ef4444" : "none"} stroke={isFavorite(story.id) ? "#ef4444" : "currentColor"} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Story content */}
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
          </div>
        }
      >
        <StoryComponent />
      </Suspense>

      {/* Story footer */}
      <div className={`max-w-3xl mx-auto px-6 py-12 text-center border-t ${isNightMode ? "border-amber-800" : "border-gray-200 dark:border-gray-700"}`}>
        <p className={`text-sm italic mb-4 ${isNightMode ? "text-amber-300" : "text-gray-500"}`}>
          "{story.moral}"
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to={`/age/${story.ageGroup}`}
            className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-sm transition-colors"
          >
            More stories for ages {story.ageGroup}
          </Link>
        </div>
      </div>
    </div>
  );
}
